// smsImagesApi — client access to the customer photo-upload feature (B).
//
// Dispatch requests an upload link for a number (minted server-side, texted to
// the customer) and lists the photos that came back. All storage access is
// server-mediated (Admin SDK); the browser only ever sees short-lived signed
// URLs. Same Authorization: Bearer pattern as forwardedSmsApi.

import { auth } from './firebase';
import { toE164 } from './phone';
import { USE_SUPABASE, supabase } from './supabaseClient';

const TOKEN_FN = '/.netlify/functions/sms-upload-token';
const LIST_FN = '/.netlify/functions/sms-images-list';

export interface SmsImage {
  id: string;
  url: string;
  contentType: string | null;
  uploadedAt: number;
  /** 'in' = the customer sent it; 'out' = a share row (an MMS, a link upload's thread twin, or a picture dispatch sent). Postgres only. */
  direction?: 'in' | 'out';
  /** 'link' (the /u/ upload page), 'mms', 'customer_upload' (the thread twin of a link upload), null (sent by dispatch). Postgres only. */
  source?: string | null;
}

/** The staff session as a Bearer header for the photo functions (also used by customerIdentities for the
 *  website ID check's photos, which the same dispatch gate serves). */
export async function authHeader(): Promise<Record<string, string>> {
  // Supabase mode has no Firebase session — send the Supabase access token
  // instead; _email-auth routes on the token's issuer and accepts both.
  if (USE_SUPABASE) {
    const { data } = await supabase().auth.getSession();
    const token = data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
  const token = await auth?.currentUser?.getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function readError(res: Response, fallback: string): Promise<string> {
  const detail = await res.json().catch(() => ({}));
  return detail.error || `${fallback} ${res.status}`;
}

/** Mint a one-time upload token for a number; returns the public upload URL to
 *  text to the customer. purpose 'verification' makes the upload page show the
 *  guided ID + selfie flow instead of the generic photo tray. */
export async function requestUploadLink(number: string, purpose?: 'verification'): Promise<string> {
  const res = await fetch(TOKEN_FN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ phone: toE164(number), ...(purpose ? { purpose } : {}) }),
  });
  if (!res.ok) throw new Error(await readError(res, 'sms-upload-token'));
  const { token } = await res.json();
  return `${window.location.origin}/u/${token}`;
}

const SHARE_FN = '/.netlify/functions/sms-share-upload';

/** Upload a dispatcher-chosen photo and get back the short public view link
 *  (/p/<token>) to text to the customer. */
/** Upload one photo into a share batch. Passing the token returned by the
 *  previous call APPENDS to the same batch, so a multi-photo pick becomes ONE
 *  /p/ link that opens ALL of them. */
export async function sharePhoto(
  number: string,
  dataUrl: string,
  batchToken?: string
): Promise<{ url: string; token: string }> {
  const res = await fetch(SHARE_FN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({
      phone: toE164(number),
      dataUrl,
      contentType: 'image/jpeg',
      ...(batchToken ? { batchToken } : {}),
    }),
  });
  if (!res.ok) throw new Error(await readError(res, 'sms-share-upload'));
  const { token } = await res.json();
  return { url: `${window.location.origin}/p/${token}`, token };
}

/** True when a URL is one of our photo-share links (used to render the picture
 *  inline in the chat bubble instead of a bare link). */
export function isSharePhotoUrl(url: string): boolean {
  return /\/p\/[a-f0-9]{48}(?:$|[^a-f0-9])/.test(url) || /sms-share-view\?t=[a-f0-9]{48}/.test(url);
}

/** First share-photo URL inside a message body, or null. */
export function extractSharePhotoUrl(body: string): string | null {
  for (const url of body.match(/https?:\/\/[^\s<>"')]+/g) || []) {
    if (isSharePhotoUrl(url)) return url;
  }
  return null;
}

/** List a number's uploaded photos (each a short-lived signed URL). */
export async function listImages(number: string): Promise<SmsImage[]> {
  return readImages(`phone=${encodeURIComponent(toE164(number))}`);
}

/** The photos of a customer pair's number, asked by the pair's id (0136, the ID reviews queue) so the number is
 *  never sent; for everyone but the owner they come back as sealed links that carry no number either. */
export async function listImagesForIdentity(ref: string): Promise<SmsImage[]> {
  return readImages(`ref=${encodeURIComponent(ref)}`);
}

async function readImages(query: string): Promise<SmsImage[]> {
  const res = await fetch(`${LIST_FN}?${query}`, {
    headers: { ...(await authHeader()) },
  });
  if (!res.ok) throw new Error(await readError(res, 'sms-images-list'));
  const data = await res.json();
  return Array.isArray(data.images) ? data.images : [];
}
